// Product search: filter + text + page -> `products/find.json`. Commands replace the old
// store-subscription chains: `setSearchText` (debounced 250 ms), `setFilter`, `setPage`.
import * as api from '$api/endpoints.js';

/** @typedef {import('$lib/types').Product} Product */

const SEARCH_DEBOUNCE_MS = 250;

export function defaultFilter() {
	return {
		/** @type {Array<{ id: number | string, name: string }>} */
		selected_brands: [],
		/** @type {Array<{ id: number, name: string }>} */
		selected_ingredients: [],
		/** Products containing any of these are dropped. @type {Array<{ id: number, name: string }>} */
		excluded_ingredients: [],
		/** @type {any} */
		selected_category: null,
		/** @type {number[]} */
		product_types: [],
		/** @type {number[]} */
		product_forms: [],
		/** @type {string[]} */		
		product_tags: [],
		specials: false,
		/** @type {number[]} */
		selected_supplier_types: [],
		page: 1
	};
}

class ProductsStore {
	/** @type {Product[]} */
	results = $state.raw([]);
	page = $state(1);
	count = $state(0);
	loading = $state(false);
	/** True once the first search has completed. */
	initialised = $state(false);
	filter = $state(defaultFilter());
	searchText = $state('');

	/** @type {ReturnType<typeof setTimeout> | undefined} */
	#timer;
	#seq = 0;
	/** @type {AbortController | null} */
	#abort = null;

	get isFiltered() {
		const f = this.filter;
		return Boolean(
			f.selected_brands.length ||
			f.product_types.length ||
			f.product_forms.length ||
			f.selected_ingredients.length ||
			f.excluded_ingredients.length ||
			f.selected_category ||
			this.searchText.length ||
			f.product_tags.length ||
			f.specials
		);
	}

	/** Run the first search if none has happened yet (products page mount). */
	ensureLoaded() {
		if (!this.initialised && !this.loading) return this.search();
		return Promise.resolve();
	}

	/**
	 * Debounced text search; clearing the text searches immediately.
	 * @param {string} text
	 */
	setSearchText(text) {
		this.searchText = text;
		clearTimeout(this.#timer);
		if (!text) {
			this.filter.page = 1;
			return this.search();
		}
		this.#timer = setTimeout(() => {
			this.filter.page = 1;
			this.search();
		}, SEARCH_DEBOUNCE_MS);
	}

	/**
	 * Merge facet changes and search from page 1.
	 * @param {Partial<ReturnType<typeof defaultFilter>>} patch
	 */
	setFilter(patch) {
		Object.assign(this.filter, patch);
		this.filter.page = 1;
		return this.search();
	}

	/** @param {number} page */
	setPage(page) {
		this.filter.page = page;
		return this.search();
	}

	clearFilters() {
		clearTimeout(this.#timer);
		this.searchText = '';
		Object.assign(this.filter, defaultFilter());
		return this.search();
	}

	/** Fetch the current page; falls back to page 1 when the page is out of range. */
	async search() {
		clearTimeout(this.#timer);
		this.#abort?.abort();
		const controller = new AbortController();
		this.#abort = controller;
		const seq = ++this.#seq;

		this.loading = true;
		try {
			let response = await api.findProducts(
				$state.snapshot(this.filter),
				this.searchText,
				controller.signal
			);
			if (seq !== this.#seq) return;
			if (
				response &&
				(!response.products || response.products.length === 0) &&
				response.page > 1
			) {
				this.filter.page = 1;
				response = await api.findProducts(
					$state.snapshot(this.filter),
					this.searchText,
					controller.signal
				);
				if (seq !== this.#seq) return;
			}
			if (response) {
				this.results = response.products ?? [];
				this.page = Number(response.page) || 1;
				this.count = Number(response.count) || 0;
				this.initialised = true;
			}
		} catch (e) {
			if (!(e instanceof Error && e.name === 'AbortError'))
				console.error('Product search failed', e);
		} finally {
			if (seq === this.#seq) this.loading = false;
		}
	}
}

export const products = new ProductsStore();
